import { useSearchParams } from "react-router-dom";
import { parseISO } from "date-fns";
import WeekViewItem from "./WeekViewItem/WeekViewItem";
import {
    DayMapping,
    DayOfWeek,
    IWeeklySchedule,
    IWeeklyScheduleDashboard,
} from "../../../../schedule/types/schedule.types";
import moment from "../../../../../utils/moment";
import ListState from "../../../../../components/ListState/ListState";
import EmptyWeekView from "./EmptyWeekView";
import useNewBackground from "../../../../../hooks/useNewBackground";
import useGetWeekViewData from "../../../hooks/useGetWeekViewData";
import { formatDate, isDateInCurrentWeek } from "../../../../../utils/helpers";

export default function MyWeekView() {
    const [searchParams] = useSearchParams();

    const useNewBg = useNewBackground();

    const dayTitleClasses =
        "bg-[rgba(241,241,243,0.40)] py-8 text-xs uppercase font-medium text-[var(--text-soft-400)] text-center";
    const dayItemsWrapperClass = `min-w-[150px] flex flex-col border-r border-r-strokedark ${
        useNewBg ? "bg-[rgba(241,241,243,0.40)]" : ""
    }`;
    const dayItemWrapperClass = `h-[184px] p-8 w-full border-t border-t-strokedark ${
        useNewBg ? "bg-[rgba(255,255,255,0.64)]" : ""
    }`;

    const startOfWeekFallback = moment()
        .startOf("isoWeek")
        .clone()
        .startOf("isoWeek")
        .format("YYYY-MM-DD");

    const startOfWeek = searchParams.get("from_date") || startOfWeekFallback;

    const isCurrentWeek = isDateInCurrentWeek(parseISO(startOfWeek));

    const {
        data: weekData,
        loading: weekLoading,
        error: weekError,
        isSuccess: weekIsSuccess,
    } = useGetWeekViewData();

    const allSchedulesByDay: { [key: string]: IWeeklyScheduleDashboard[] } = {
        sun: [],
        mon: [],
        tue: [],
        wed: [],
        thu: [],
        fri: [],
        sat: [],
    };
    const startOfWeekMoment = moment(startOfWeek, "YYYY-MM-DD");

    const firstDay =
        startOfWeekMoment.day() === 0
            ? startOfWeekMoment.clone().add(1, "day")
            : startOfWeekMoment.clone().startOf("isoWeek");

    const daysOfWeekFormatted = Array.from({ length: 7 }).map((_, index) =>
        firstDay.clone().add(index, "days").format("D ddd")
    );

    // Group schedules by day of the week based on session_start_date
    const seenSchedules = new Set();

    const dayOfWeekMap: DayMapping = {
        Sun: "sun",
        Mon: "mon",
        Tue: "tue",
        Wed: "wed",
        Thu: "thu",
        Fri: "fri",
        Sat: "sat",
    };

    if (isCurrentWeek) {
        weekData?.data
            ?.filter((item) => !item.is_cancelled)
            .forEach((s) => {
                // Get the day of the week
                const scheduleDay = formatDate(
                    s.start_date_time || "",
                    false,
                    "eee"
                );

                const dayOfWeek = dayOfWeekMap[scheduleDay as DayOfWeek];

                const schedule = {
                    ...s,
                    start: s.start_date_time,
                    end: s.end_date_time,
                };

                const scheduleKey = `${s.session_day}_${s.session_start_hr}_${
                    s.session_start_min
                }_${s.session_end_hr}_${s.session_end_min}_${
                    s.client.first_name
                }_${s.client.last_name}_${s.service}_${s.session_id}_${
                    s.session_history_id ?? "history_id"
                }`;

                if (!seenSchedules.has(scheduleKey)) {
                    allSchedulesByDay[dayOfWeek]?.push({
                        ...schedule,
                        cancelled_by: null,
                    });
                    seenSchedules.add(scheduleKey);
                }
            });
    } else {
        weekData?.data
            ?.filter((item) => !item.is_cancelled)
            .forEach((s) => {
                // Get the day of the week
                const scheduleDay = s.session_day;
                const dayOfWeek = dayOfWeekMap[scheduleDay as DayOfWeek];

                // Set start time directly using session_start_hr and session_start_min
                const startTime = moment()
                    .set("hour", s.session_start_hr)
                    .set("minute", s.session_start_min)
                    .set("second", 0)
                    .set("millisecond", 0);

                // Set end time similarly
                const endTime = moment()
                    .set("hour", s.session_end_hr)
                    .set("minute", s.session_end_min)
                    .set("second", 0)
                    .set("millisecond", 0);

                const schedule = {
                    ...s,
                    start: startTime.toISOString(),
                    end: endTime.toISOString(),
                };

                const scheduleKey = `${s.session_day}_${s.session_start_hr}_${s.session_start_min}_${s.session_end_hr}_${s.session_end_min}_${s.client.first_name}_${s.client.last_name}_${s.service}_${s.session_id}`;

                if (!seenSchedules.has(scheduleKey)) {
                    allSchedulesByDay[dayOfWeek]?.push({
                        ...schedule,
                        cancelled_by: null,
                    });
                    seenSchedules.add(scheduleKey);
                }
            });
    }

    const padArrayToLength = <T,>(
        arr: T[],
        length: number,
        fillValue = null
    ): (T | null)[] => {
        return arr.length >= length
            ? arr.slice(0, length)
            : [...arr, ...Array(length - arr.length).fill(fillValue)];
    };

    const longestArray = Object.values(allSchedulesByDay).reduce((a, b) =>
        a.length > b.length ? a : b
    );
    const maxItems = longestArray.length;

    const paddedSchedulesByDay = Object.fromEntries(
        Object.entries(allSchedulesByDay).map(([day, schedules]) => [
            day,
            padArrayToLength(schedules, maxItems),
        ])
    ) as { [key: string]: (IWeeklySchedule | null)[] };

    const weekViewCardsToDisplay = (day: string) => {
        return paddedSchedulesByDay[day].map((item, i) => {
            if (item) {
                return (
                    <div
                        key={item.session_id + 1}
                        className={dayItemWrapperClass}
                    >
                        <WeekViewItem schedule={item} />
                    </div>
                );
            }
            return (
                <EmptyWeekView
                    className={dayItemWrapperClass}
                    key={`${i + 1}`}
                />
            );
        });
    };

    return (
        <>
            {weekLoading ? (
                <div
                    className="flex items-center justify-center h-[350px]
                    "
                    data-testid="loader"
                >
                    <span className="mid-loader" />
                </div>
            ) : null}
            {weekError ? (
                <div className="h-full text-sm font-medium flex items-center justify-center">
                    <ListState
                        isError
                        errorMsg={
                            weekError?.response?.data?.message ||
                            "Could not load schedule at this time"
                        }
                        stateHelperText="Try reloading this page to solve this issue"
                        context="general"
                        cardClass="h-[350px]"
                        mode={useNewBg ? "new" : "old"}
                    />
                </div>
            ) : null}
            {weekIsSuccess && weekData && weekData.data.length === 0 ? (
                <ListState
                    stateHelperText="No schedule found at this time"
                    isError={false}
                    emptyMessage="No schedule found"
                    context="general"
                    cardClass="h-[350px]"
                    mode={useNewBg ? "new" : "old"}
                />
            ) : null}
            {weekIsSuccess && weekData && weekData.data.length > 0 ? (
                <div
                    className={`rounded-r12 border ${
                        useNewBg
                            ? "bg-[rgba(255,255,255,0.40)] border-[#E7E7E7]"
                            : "bg-white border-strokedark"
                    } grid grid-cols-[40px,repeat(7,minmax(150px,1fr))] w-full max-w-full overflow-x-auto`}
                >
                    <div className="w-40 flex flex-col border-r border-r-strokedark">
                        <div className="bg-[rgba(241,241,243,0.40)] border-b py-[4px] border-b-strokedark rounded-tl-r12">
                            <span className="h-32 w-full block" />
                        </div>
                        <div className="flex-1" />
                    </div>
                    <div className={dayItemsWrapperClass}>
                        <div className={dayTitleClasses}>
                            {daysOfWeekFormatted[0]}
                        </div>
                        {weekViewCardsToDisplay("mon")}
                    </div>
                    <div className={dayItemsWrapperClass}>
                        <div className={dayTitleClasses}>
                            {daysOfWeekFormatted[1]}
                        </div>
                        {weekViewCardsToDisplay("tue")}
                    </div>
                    <div className={dayItemsWrapperClass}>
                        <div className={dayTitleClasses}>
                            {daysOfWeekFormatted[2]}
                        </div>
                        {weekViewCardsToDisplay("wed")}
                    </div>
                    <div className={dayItemsWrapperClass}>
                        <div className={dayTitleClasses}>
                            {daysOfWeekFormatted[3]}
                        </div>
                        {weekViewCardsToDisplay("thu")}
                    </div>
                    <div className={dayItemsWrapperClass}>
                        <div className={dayTitleClasses}>
                            {daysOfWeekFormatted[4]}
                        </div>
                        {weekViewCardsToDisplay("fri")}
                    </div>
                    <div className={dayItemsWrapperClass}>
                        <div className={dayTitleClasses}>
                            {daysOfWeekFormatted[5]}
                        </div>
                        {weekViewCardsToDisplay("sat")}
                    </div>
                    <div className={`${dayItemsWrapperClass} border-r-0`}>
                        <div className={`${dayTitleClasses} !border-none`}>
                            {daysOfWeekFormatted[6]}
                        </div>
                        {weekViewCardsToDisplay("sun")}
                    </div>
                </div>
            ) : null}
        </>
    );
}
